import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Calendar as CalendarIcon, HelpCircle, LogOut,
  Trash2, Plus, Check, ChevronLeft, ChevronRight, Pencil, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const MONTHS_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const DAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const STATUS_LABELS = { neu: "Neu", bestaetigt: "Bestätigt", erledigt: "Erledigt", abgesagt: "Abgesagt" };
const STATUS_COLORS = {
  neu: "bg-[#EBE3D5] text-[#A66B56]",
  bestaetigt: "bg-[#5C6B5E]/15 text-[#5C6B5E]",
  erledigt: "bg-[#2A332C]/10 text-[#2A332C]",
  abgesagt: "bg-red-100 text-red-700",
};

function toISO(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function matrix(year, month) {
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("anmeldungen");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C6B5E]">Lädt…</div>;
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-[#2A332C] text-[#FDFBF7] p-6 flex flex-col sticky top-0 h-screen">
        <div className="mb-12">
          <p className="eyebrow mb-2" style={{ color: "#D9A889" }}>Admin</p>
          <h1 className="font-display text-2xl leading-tight">Michele Galliker</h1>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          <NavBtn active={tab === "anmeldungen"} onClick={() => setTab("anmeldungen")} icon={Users} label="Anmeldungen" testid="tab-anmeldungen" />
          <NavBtn active={tab === "verfuegbarkeit"} onClick={() => setTab("verfuegbarkeit")} icon={CalendarIcon} label="Verfügbarkeit" testid="tab-verfuegbarkeit" />
          <NavBtn active={tab === "faq"} onClick={() => setTab("faq")} icon={HelpCircle} label="FAQ" testid="tab-faq" />
        </nav>
        <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-colors" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> <span className="text-sm">Abmelden</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 sm:p-12 max-w-6xl">
        {tab === "anmeldungen" && <RegistrationsPanel />}
        {tab === "verfuegbarkeit" && <AvailabilityPanel />}
        {tab === "faq" && <FAQPanel />}
      </main>
    </div>
  );
}

function NavBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${active ? "bg-white/10 text-[#FDFBF7]" : "text-[#FDFBF7]/70 hover:bg-white/5"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-[1.5rem] border border-[#5C6B5E]/10 shadow-sm ${className}`}>{children}</div>;
}

function RegistrationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/registrations"); setItems(data); }
    catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/registrations/${id}`, { status });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Anmeldung wirklich löschen?")) return;
    await api.delete(`/registrations/${id}`);
    load();
  };

  return (
    <div>
      <header className="mb-10">
        <p className="eyebrow mb-3">Übersicht</p>
        <h2 className="font-display text-4xl text-[#2A332C]">Anmeldungen</h2>
        <p className="text-[#5C6B5E] mt-2">{items.length} Einträge</p>
      </header>
      {loading ? <p className="text-[#5C6B5E]">Lädt…</p> : items.length === 0 ? (
        <Card className="p-12 text-center text-[#5C6B5E]">Noch keine Anmeldungen.</Card>
      ) : (
        <div className="space-y-4" data-testid="registrations-list">
          {items.map((r) => (
            <Card key={r.id} className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-display text-2xl text-[#2A332C]">{r.vorname} {r.name}</h3>
                    <span className={`text-xs px-3 py-1 rounded-full ${STATUS_COLORS[r.status]}`}>{STATUS_LABELS[r.status]}</span>
                  </div>
                  <p className="text-[#5C6B5E] text-sm">Termin: <strong className="text-[#2A332C]">{r.termin}</strong></p>
                  <p className="text-[#5C6B5E] text-sm">Adresse: {r.adresse}</p>
                  <p className="text-[#5C6B5E] text-sm">Telefon: <a href={`tel:${r.telefon}`} className="underline">{r.telefon}</a></p>
                  {r.email && <p className="text-[#5C6B5E] text-sm">E-Mail: <a href={`mailto:${r.email}`} className="underline">{r.email}</a></p>}
                  {r.nachricht && <p className="text-[#5C6B5E] text-sm mt-3 italic">"{r.nachricht}"</p>}
                  <p className="text-xs text-[#5C6B5E]/70 mt-3">Eingegangen: {new Date(r.created_at).toLocaleString("de-CH")}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="input-elegant text-sm py-2 px-3 min-w-[140px]" data-testid={`reg-status-${r.id}`}>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <button onClick={() => remove(r.id)} className="p-2 rounded-full hover:bg-red-50 text-red-600" title="Löschen" data-testid={`reg-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AvailabilityPanel() {
  const today = new Date();
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [avail, setAvail] = useState([]);

  const load = async () => {
    const { data } = await api.get("/availability"); setAvail(data);
  };
  useEffect(() => { load(); }, []);

  const availSet = useMemo(() => new Set(avail.filter((a) => a.available).map((a) => a.date)), [avail]);
  const cells = matrix(current.getFullYear(), current.getMonth());

  const toggle = async (iso) => {
    const isAvailable = availSet.has(iso);
    await api.post("/availability", { date: iso, available: !isAvailable });
    load();
  };

  return (
    <div>
      <header className="mb-10">
        <p className="eyebrow mb-3">Verfügbarkeit</p>
        <h2 className="font-display text-4xl text-[#2A332C]">Termine verwalten</h2>
        <p className="text-[#5C6B5E] mt-2">Klicke auf einen Tag, um Verfügbarkeit ein- oder auszuschalten.</p>
      </header>
      <Card className="p-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1))} className="p-2 rounded-full hover:bg-[#F4EFE6]" data-testid="admin-cal-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="font-display text-2xl text-[#2A332C]">{MONTHS_DE[current.getMonth()]} {current.getFullYear()}</h3>
          <button onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1))} className="p-2 rounded-full hover:bg-[#F4EFE6]" data-testid="admin-cal-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {DAYS_DE.map((d) => <div key={d} className="text-[11px] uppercase tracking-widest text-[#5C6B5E] py-3">{d}</div>)}
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const iso = toISO(d);
            const isAvail = availSet.has(iso);
            return (
              <button key={i} onClick={() => toggle(iso)} data-testid={`admin-cal-day-${iso}`} className={`aspect-square flex items-center justify-center rounded-full text-sm transition-all ${isAvail ? "bg-[#5C6B5E] text-white font-medium" : "text-[#2A332C] hover:bg-[#EBE3D5]"}`}>
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function FAQPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item or 'new'
  const [form, setForm] = useState({ question: "", answer: "", order: 0 });
  const [error, setError] = useState("");

  const load = async () => { const { data } = await api.get("/faq"); setItems(data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ question: "", answer: "", order: items.length + 1 }); setEditing("new"); setError(""); };
  const startEdit = (it) => { setForm({ question: it.question, answer: it.answer, order: it.order }); setEditing(it.id); setError(""); };
  const cancel = () => { setEditing(null); setError(""); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/faq", form);
      else await api.put(`/faq/${editing}`, form);
      setEditing(null); load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("FAQ wirklich löschen?")) return;
    await api.delete(`/faq/${id}`); load();
  };

  return (
    <div>
      <header className="mb-10 flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Inhalte</p>
          <h2 className="font-display text-4xl text-[#2A332C]">FAQ verwalten</h2>
        </div>
        <button onClick={startNew} className="btn-primary text-sm py-2 px-5 flex items-center gap-2" data-testid="faq-new">
          <Plus className="w-4 h-4" /> Neu
        </button>
      </header>

      {editing && (
        <Card className="p-6 mb-6" >
          <div className="space-y-4" data-testid="faq-edit-form">
            <input className="input-elegant" placeholder="Frage" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-edit-question" />
            <textarea className="input-elegant" style={{ minHeight: 120 }} placeholder="Antwort" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-edit-answer" />
            <input type="number" className="input-elegant" placeholder="Reihenfolge" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="faq-edit-order" />
            {error && <p className="text-sm text-red-700">{error}</p>}
            <div className="flex gap-3">
              <button onClick={save} className="btn-primary text-sm py-2 flex items-center gap-2" data-testid="faq-save"><Check className="w-4 h-4" /> Speichern</button>
              <button onClick={cancel} className="btn-outline text-sm py-2 flex items-center gap-2" data-testid="faq-cancel"><X className="w-4 h-4" /> Abbrechen</button>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3" data-testid="faq-list">
        {items.map((it) => (
          <Card key={it.id} className="p-5 flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-display text-xl text-[#2A332C]">{it.question}</p>
              <p className="text-[#5C6B5E] text-sm mt-1">{it.answer}</p>
              <p className="text-xs text-[#5C6B5E]/60 mt-2">Reihenfolge: {it.order}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(it)} className="p-2 rounded-full hover:bg-[#F4EFE6]" data-testid={`faq-edit-${it.id}`}><Pencil className="w-4 h-4 text-[#5C6B5E]" /></button>
              <button onClick={() => remove(it.id)} className="p-2 rounded-full hover:bg-red-50" data-testid={`faq-delete-${it.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
